"use client";

import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { TopBar } from "@/components/top-bar";
import { TopNav } from "@/components/top-nav";
import { cn } from "@/lib/utils";
import { usePlayer } from "@/context/player-context";
import { VideoPlayer } from "@/components/video-player";

export function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const {
    streamUrl, mediaTitle, videoMode, setVideoMode, totalDuration,
    subtitleTracks, startPosition, hasNext, hasPrev,
    playNext, playPrev, reportProgress, closePlayer
  } = usePlayer();
  const isAuthPage = pathname === "/login" || pathname === "/setup";

  const isDiscoveryMode = pathname.startsWith("/discover") ||
                          pathname.startsWith("/requests");

  if (isAuthPage) {
    return <main className="min-h-screen w-full">{children}</main>;
  }

  // Memoised so that mode changes (mini ↔ large) don't recreate the sources array,
  // which would trigger useEffect([options.sources]) in VideoPlayer and restart playback from 0.
  const playerOptions = useMemo(() => ({
    autoplay: true,
    sources: streamUrl ? [{ src: streamUrl, type: 'application/x-mpegURL' }] : [],
    duration: totalDuration,
    html5: {
      vhs: { overrideNative: true },
      hls: { overrideNative: true }
    }
  }), [streamUrl, totalDuration]); // eslint-disable-line react-hooks/exhaustive-deps

  const playerNode = streamUrl ? (
    <>
      {/* Glass layer in mini mode — must live outside the player stacking context
          so its backdrop-filter can sample the page content directly */}
      {videoMode === 'mini' && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[59] pointer-events-none"
          style={{
            height: '140px',
            background: 'rgba(12, 12, 14, 0.78)',
            backdropFilter: 'blur(10px) saturate(150%)',
            WebkitBackdropFilter: 'blur(10px) saturate(150%)',
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}
        />
      )}
      <div
        className={cn(
          videoMode === 'mini'
            ? "fixed inset-0 w-full h-full z-[60] pointer-events-none"
            : "fixed inset-0 w-full h-full z-[70] bg-black pointer-events-auto"
        )}
      >
        <VideoPlayer
          options={playerOptions}
          title={mediaTitle || ''}
          mode={videoMode}
          onModeChange={setVideoMode}
          subtitleTracks={subtitleTracks}
          startPosition={startPosition}
          hasNext={hasNext}
          hasPrev={hasPrev}
          onNext={playNext}
          onPrev={playPrev}
          onProgress={reportProgress}
          onClose={closePlayer}
        />
      </div>
    </>
  ) : null;

  // 1. DISCOVERY LAYOUT
  if (isDiscoveryMode) {
    return (
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <div className="flex-1 ml-64 flex flex-col">
          <TopBar />
          <main className="flex-1 pt-20 pb-20 overflow-auto">
            <div className="p-6 lg:p-10 max-w-(--breakpoint-2xl) mx-auto">
              {children}
            </div>
          </main>
        </div>
        <div className="fixed inset-0 -z-10 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.02)_0%,transparent_50%)] pointer-events-none" />
        {playerNode}
      </div>
    );
  }

  // 2. MAIN LAYOUT
  return (
    <div className="flex min-h-screen w-full flex-col bg-background">
      <TopNav />
      <main className="flex-1 pt-16 pb-20 overflow-auto">
        <div className={cn(pathname === "/" ? "" : "p-6 lg:p-12 max-w-(--breakpoint-2xl) mx-auto")}>
          {children}
        </div>
      </main>
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.03)_0%,transparent_50%)] pointer-events-none" />
      {playerNode}
    </div>
  );
}
